/**
 * Encode a JS value (object, array, primitive) to a Base64 string.
 */
export function encodeToBase64(obj: unknown): string {
  const json = JSON.stringify(obj);
  // btoa only accepts “binary” strings, so first UTF-8 → Latin1
  const latin1 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p) =>
    String.fromCharCode(parseInt(p, 16))
  );
  return btoa(latin1);
}

/**
 * Decode a Base64 string back into the original JS value.
 */
export function decodeFromBase64<T = unknown>(b64: string): T {
  // atob → Latin1 → percent-encoded UTF-8 → decodeURIComponent
  const latin1 = atob(b64);
  const json = latin1
    .split("")
    .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
    .join("");
  return JSON.parse(decodeURIComponent(json)) as T;
}

export default function objectToQueryString(obj: object): string {
  return (
    "?" +
    Object.entries(obj)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&")
  );
}

// helper for "12:35pm, nov 13" style formatting
function formatWithTime(date: Date, now: Date): string {
  // time part: 12:35pm
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const minuteStr = minutes.toString().padStart(2, "0");
  const timePart = `${hours}:${minuteStr}${ampm}`;

  // date part: nov 13 or nov 13, 2025
  const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;
  const month = MONTHS[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const sameYear = year === now.getFullYear();

  if (sameYear) {
    return `${timePart}, ${month} ${day}`;
  }
  return `${timePart}, ${month} ${day}, ${year}`;
}

export function formatRelativeTime(
  input: string | number | Date,
  showTime: boolean = false
): string {
  const date = new Date(input);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHrs = Math.floor(diffMin / 60);

  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  // ⏱️ Under 1 minute
  if (diffSec < 60) return "just now";

  // ⏱️ Under 1 hour
  if (diffMin < 60) return diffMin === 1 ? "1 min ago" : `${diffMin} mins ago`;

  // ⏱️ Under 24 hours
  if (diffHrs < 24) {
    if (diffHrs === 1) return "1 hour ago";
    return `${diffHrs} hours ago`;
  }

  // 📅 Same day
  if (isToday) return "today";

  // 🕰️ Yesterday
  if (isYesterday) return "yesterday";

  // 📆 Older → either just date or date+time
  if (!showTime) {
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  return formatWithTime(date, now);
}
